import { ArtworkPrintSettings } from "@/components/artwork/artworkPrintSettings";
import { PhotographyPrintSettings } from "@/components/photography/photographyPrintSettings";
import { SupportedLocale } from "@/i18n";
import api from "@/lib/axiosConfig";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";

type GenerateArtworksPdfParams = {
  lang: string;
  template: string;
  settings: ArtworkPrintSettings;
  artworkIds: string[];
  orderBy: object[];
};

type GenerateArtworkSinglePdfParams = {
  lang: string;
  template: string;
  settings: ArtworkPrintSettings;
  artworkId: string;
};

export const baseRoute = "back/api/pdf";

export const administrativeDocumentTypeSchema = z.enum([
  "document1",
  "document2",
]);

export type AdministrativeDocumentType = z.infer<
  typeof administrativeDocumentTypeSchema
>;

export const generateAdministrativeDocumentSchema = z.object({
  oeuvreExaminee: z.boolean(),
  signloca: z.string(),
  documentType: administrativeDocumentTypeSchema,
});

export type GenerateAdministrativeDocument = z.infer<
  typeof generateAdministrativeDocumentSchema
>;

const generateSingleArtworksPdf = async (
  data: GenerateArtworkSinglePdfParams,
) => {
  return await api
    .post<File>("back/api/pdf/artwork-single", data, {
      responseType: "blob",
    })
    .then((res) => res.data);
};

export const useGenerateSingleArtworksPdf = (
  options?: Omit<
    UseMutationOptions<File, Error, GenerateArtworkSinglePdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateSingleArtworksPdf,
  });

const generateArtworksPdf = async (data: GenerateArtworksPdfParams) => {
  return await api
    .post<File>("back/api/pdf/artworks", data, {
      responseType: "blob",
    })
    .then((res) => res.data);
};

export const useGenerateArtworksPdf = (
  options?: Omit<
    UseMutationOptions<File, Error, GenerateArtworksPdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateArtworksPdf,
  });

type GeneratePhotographiesPdfParams = {
  lang: string;
  template: string;
  settings: PhotographyPrintSettings;
  photographyIds: string[];
  orderBy: object[];
};

const generatePhotographiesPdf = async (
  data: GeneratePhotographiesPdfParams,
) => {
  return await api
    .post<File>("back/api/pdf/photographies", data, {
      responseType: "blob",
    })
    .then((res) => res.data);
};

export const useGeneratePhotographiesPdf = (
  options?: Omit<
    UseMutationOptions<File, Error, GeneratePhotographiesPdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generatePhotographiesPdf,
  });

type GeneratePhotographiesSinglePdfParams = {
  lang: string;
  template: string;
  settings: PhotographyPrintSettings;
  photographyId: string;
};

const generatePhotographiesSinglePdf = async (
  data: GeneratePhotographiesSinglePdfParams,
) => {
  return await api
    .post<File>("back/api/pdf/photography-single", data, {
      responseType: "blob",
    })
    .then((res) => res.data);
};

export const useGeneratePhotographiesSinglePdf = (
  options?: Omit<
    UseMutationOptions<File, Error, GeneratePhotographiesSinglePdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generatePhotographiesSinglePdf,
  });

const generateAdministrativeDocumentsPdf = async ({
  params,
  artworkId,
  lang,
}: {
  params: GenerateAdministrativeDocument;
  artworkId: string;
  lang: SupportedLocale;
}) => {
  const { data } = await api.post<File>(
    `${baseRoute}/administrative-document/${artworkId}`,
    params,
    { params: { lang }, responseType: "blob" },
  );
  return data;
};

export const useGenerateAdministrativeDocumentsPdf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateAdministrativeDocumentsPdf,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", v.artworkId, "administrative-documents"],
      });
    },
  });
};
